import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  ButtonGroup,
  Modal,
} from "react-bootstrap";
import { TiTickOutline } from "react-icons/ti";
import { IoEye } from "react-icons/io5";
import "./approved-leave.css";
import LeaveLayout from "../../components/LeaveLayout.jsx";

export default function ApprovedLeave() {
  const [showModal, setShowModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // State to hold the search term
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [rowsPerPage, setRowsPerPage] = useState(15); // Number of rows per page

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date) ? "-" : dateFormatter.format(date);
  };

  // Fetch Approved Leaves with Search and Pagination
  const fetchApprovedLeaves = async (searchQuery = "", page = 1, rowsPerPage = 15) => {
    try {
      const response = await fetch(
        `https://backend.ems.uk.hiradentallab.com/api/leaves/approved?search=${searchQuery}&page=${page}&rowsPerPage=${rowsPerPage}` // Pass search term and pagination
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRows(data.approvedLeaves || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Handle Search Input Change with Debouncing
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Debounce the search term to avoid calling the API on every keystroke
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchApprovedLeaves(searchTerm, currentPage, rowsPerPage); // Call with the search term and pagination
    }, 500); // Delay of 500ms

    return () => clearTimeout(debounceTimer); // Cleanup timeout if the component is unmounted or searchTerm changes
  }, [searchTerm, currentPage, rowsPerPage]); // Trigger effect when the search term, currentPage, or rowsPerPage changes

  // Handle Page Change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Pagination UI Logic
  const pageRange = () => {
    const totalPages = Math.ceil(rows.length / rowsPerPage);
    const range = [];
    for (let i = 1; i <= totalPages; i++) {
      range.push(i);
    }
    return range;
  };

  const handleShowModal = (rowData) => {
    setSelectedRowData(rowData);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRowData(null);
  };

  if (loading) {
    return (
      <LeaveLayout>
        <div className="loading">Loading...</div>
      </LeaveLayout>
    );
  }

  if (error) {
    return (
      <LeaveLayout>
        <div className="error">Error: {error}</div>
      </LeaveLayout>
    );
  }

  return (
    <LeaveLayout>
      <div>
        <h1 className="approved-leave-header">
          <TiTickOutline className="icon-tick" /> Approved Leave
        </h1>
        <hr />
        <div className="approved-leave-content">
          <div>
            <div className="filter-container">
              <Col xs={12} md={6} className="filter-left">
                <Dropdown className="d-inline mx-2" autoClose="inside">
                  <Dropdown.Toggle id="dropdown-autoclose-inside">
                    Show Entries
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="custom-dropdown-menu">
                    <Dropdown.Item
                      onClick={() => setRowsPerPage(15)}
                    >
                      15
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setRowsPerPage(25)}
                    >
                      25
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setRowsPerPage(50)}
                    >
                      50
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={12} md={6} className="filter-right">
                <Form>
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    value={searchTerm}
                    onChange={handleSearchChange} // Update search query dynamically
                  />
                </Form>
              </Col>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead className="approved-leave-table-header">
                  <tr className="table-header">
                    <th>SR</th>
                    <th>Name</th>
                    <th>Leave Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Applied Date</th>
                    <th>HR Approval</th>
                    <th>Admin Approval</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id} className="table-row">
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{row.name}</td>
                      <td>{row.leaveType}</td>
                      <td>{formatDate(row.startDate)}</td>
                      <td>{formatDate(row.endDate)}</td>
                      <td>{formatDate(row.appliedDate)}</td>
                      <td>{row.hrAction}</td>
                      <td>{row.adminAction}</td>
                      <td>{row.status}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShowModal(row)}
                        >
                          <IoEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-footer">
                  <tr className="pending-leave-table-footer">
                    <td colSpan="2">Total</td>
                    <td colSpan="8">{rows.length}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Pagination */}
            <div className="pagination-row">
              <div className="entries-info">
                Showing{" "}
                {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, rows.length)} of{" "}
                {rows.length} entries
              </div>
              <nav>
                <ul className="pagination no-gap">
                  <li
                    className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageRange().map((pageNumber) => (
                    <li
                      key={pageNumber}
                      className={`page-item ${
                        currentPage === pageNumber ? "active" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </a>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === Math.ceil(rows.length / rowsPerPage)
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
        {/* Stylish Modal */}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered // Center the modal on the screen
          className="custom-modal"
        >
          <Modal.Header closeButton className="custom-modal-header">
            <Modal.Title>Leave Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRowData && (
              <>
                <p>
                  <strong>Name:</strong> {selectedRowData.name}
                </p>
                <p>
                  <strong>Leave Type:</strong> {selectedRowData.leaveType}
                </p>
                <p>
                  <strong>Start Date:</strong>{" "}
                  {formatDate(selectedRowData.startDate)}
                </p>
                <p>
                  <strong>End Date:</strong>{" "}
                  {formatDate(selectedRowData.endDate)}
                </p>
                <p>
                  <strong>Applied Duration:</strong>{" "}
                  {selectedRowData.appliedDuration} day(s)
                </p>
                <p>
                  <strong>Applied Date:</strong>{" "}
                  {formatDate(selectedRowData.appliedDate)}
                </p>
                <p>
                  <strong>HR Approval:</strong> {selectedRowData.hrApproval}
                </p>
                <p>
                  <strong>Admin Approval:</strong>{" "}
                  {selectedRowData.adminApproval}
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={handleCloseModal}
              className="custom-modal-btn"
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </LeaveLayout>
  );
}
